/**
 * AgentDetails Component
 * 
 * Component for displaying and editing agent information in a popup modal.
 * Provides both view and edit modes for agent data with a clean, organized layout.
 * Based on the ClientTopBar component structure but designed as a popup.
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AgentDetails.css';
import AgentsService from '../../../AppSettings/services/AgentsService';
import { faEdit, faTimes } from '@fortawesome/free-solid-svg-icons'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * AgentDetails Component
 * 
 * Displays agent information in a popup modal with the ability to switch
 * between view and edit modes. Shows agent details including personal information,
 * contact details, and company information.
 * 
 * Features:
 * - Toggle between view and edit modes
 * - Form validation for agent information
 * - Real-time updates to agent data
 * - Popup modal overlay
 * 
 * @param {Object} props - Component props
 * @param {string} props.agentId - The ID of the agent to display
 * @param {Function} props.onClose - Callback function to close the popup
 * @param {Function} props.onAgentUpdated - Callback function when agent is updated
 * @returns {JSX.Element} A popup modal containing agent information with edit capabilities
 */
const AgentDetails = ({ agentId, onClose, onAgentUpdated }) => {
  const [agent, setAgent] = useState({});
  const [updateAgent, setUpdateAgent] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches agent information from the API
   * Updates both the display and edit state with the fetched data
   */
  const fetchAgent = useCallback(async () => {
    try {
      setLoading(true);
      const data = await AgentsService.getAgent(agentId);
      setAgent(data);
      setUpdateAgent(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching agent: ' + error);
      setError('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  // Fetch agent data when component mounts or agentId changes
  useEffect(() => {
    if (agentId) {
      fetchAgent();
    }
  }, [fetchAgent, agentId]);

  /**
   * Handles input changes in edit mode
   * Updates the updateAgent state with new values
   * 
   * @param {Event} event - The input change event
   */
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUpdateAgent({ ...updateAgent, [name]: value });
  };

  /**
   * Handles form submission in edit mode
   * Sends updated agent information to the API
   * 
   * @param {Event} event - The form submission event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await AgentsService.updateAgent(agentId, updateAgent);
      console.log('Agent details updated successfully');
      await fetchAgent();
      setEditMode(false);
      if (onAgentUpdated) {
        onAgentUpdated();
      }
    } catch (error) {
      console.error('Error updating agent details: ' + error);
      setError('Failed to update agent details');
    }
  };

  /**
   * Handles clicking outside the modal content
   * Closes the modal if the click is outside
   */
  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="agent-modal-overlay" onClick={handleOverlayClick}>
        <div className="agent-modal-content">
          <div className="agent-loading">Loading agent details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-modal-overlay" onClick={handleOverlayClick}>
        <div className="agent-modal-content">
          <div className="agent-error">
            <p>{error}</p>
            <button onClick={onClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-modal-overlay" onClick={handleOverlayClick}>
      <div className="agent-modal-content">
        {/* Modal Header */}
        <div className="agent-modal-header">
          <h2>Agent Details</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="agent-modal-body">
          {editMode ? (
            // Edit mode form
            <form onSubmit={handleSubmit} className="agent-edit-form">
              {/* Personal Information Section */}
              <div className="form-section">
                <h5>Personal Information</h5>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="FirstName">First Name:</label>
                    <input
                      type="text"
                      id="FirstName"
                      name="FirstName"
                      value={updateAgent.FirstName || ''}
                      onChange={handleInputChange}
                      placeholder="First Name"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="LastName">Last Name:</label>
                    <input
                      type="text"
                      id="LastName"
                      name="LastName"
                      value={updateAgent.LastName || ''}
                      onChange={handleInputChange}
                      placeholder="Last Name"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="ContactName">Contact Name:</label>
                    <input
                      type="text"
                      id="ContactName"
                      name="ContactName"
                      value={updateAgent.ContactName || ''}
                      onChange={handleInputChange}
                      placeholder="Contact Name"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="Title">Title:</label>
                    <input
                      type="text"
                      id="Title"
                      name="Title"
                      value={updateAgent.Title || ''}
                      onChange={handleInputChange}
                      placeholder="Job Title"
                    />
                  </div>
                </div>
              </div>

              {/* Company Information Section */}
              <div className="form-section">
                <h5>Company Information</h5>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="Company">Company:</label>
                    <input
                      type="text"
                      id="Company"
                      name="Company"
                      value={updateAgent.Company || ''}
                      onChange={handleInputChange}
                      placeholder="Company Name"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="form-section">
                <h5>Contact Information</h5>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="Email">Email:</label>
                    <input
                      type="email"
                      id="Email"
                      name="Email"
                      value={updateAgent.Email || ''}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="Phone">Phone:</label>
                    <input
                      type="tel"
                      id="Phone"
                      name="Phone"
                      value={updateAgent.Phone || ''}
                      onChange={handleInputChange}
                      placeholder="Phone Number"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information Section */}
              <div className="form-section">
                <h5>Address Information</h5>
                <div className="form-row">
                  <div className="form-field full-width">
                    <label htmlFor="Address">Address:</label>
                    <input
                      type="text"
                      id="Address"
                      name="Address"
                      value={updateAgent.Address || ''}
                      onChange={handleInputChange}
                      placeholder="Street Address"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="form-section">
                <h5>Additional Information</h5>
                <div className="form-row">
                  <div className="form-field full-width">
                    <label htmlFor="Notes">Notes:</label>
                    <textarea
                      id="Notes"
                      name="Notes"
                      value={updateAgent.Notes || ''}
                      onChange={handleInputChange}
                      placeholder="Additional notes about the agent"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="save-button">Save Changes</button>
                <button type="button" className="cancel-button" onClick={() => {setUpdateAgent(agent); setEditMode(false)}}>Cancel</button>
              </div>
            </form>
          ) : (
            // View mode display
            <div className='agent-popout'>
              {/* Agent details section */}
              <div className="agent-details-header">
                <div className="agent-name-edit">
                  <h3>{agent.FirstName} {agent.LastName}</h3>
                  <button className="agent-details-edit-button" onClick={() => setEditMode(true)}>
                    <FontAwesomeIcon icon={faEdit} style={{ fontSize: '16px' }} />
                  </button>
                </div>
                <button
                  type="button"
                  className="more-info-button"
                  onClick={() => setShowMore(prev => !prev)}
                  style={{
                    marginLeft: '8px',
                    backgroundColor: '#fff',
                    color: '#374151',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '0.35rem 0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  {showMore ? 'Show less' : 'Show more'}
                </button>
              </div>
              {showMore && (
                <div className="scroll-hint">Scroll for more info</div>
              )}

              {/* Agent information display */}
              <div className="agent-info">
                {/* Personal Information */}
                <div className="agent-info-section">
                  <h5>Personal Information</h5>
                  <div className="agent-info-item"><strong>Agent ID:</strong> {agent.AgentID || 'N/A'}</div>
                  <div className="agent-info-item"><strong>First Name:</strong> {agent.FirstName || 'N/A'}</div>
                  <div className="agent-info-item"><strong>Last Name:</strong> {agent.LastName || 'N/A'}</div>
                  <div className="agent-info-item"><strong>Full Name:</strong> {`${agent.FirstName || ''} ${agent.LastName || ''}`.trim() || 'N/A'}</div>
                  <div className="agent-info-item"><strong>Contact Name:</strong> {agent.ContactName || 'N/A'}</div>
                  <div className="agent-info-item"><strong>Title:</strong> {agent.Title || 'N/A'}</div>
                </div>
                <div className="agent-info-section">
                      <h5>Additional Notes</h5>
                      <div className="agent-info-item notes-item-full">
                        {agent.Notes || 'No additional notes available'}
                      </div>
                    </div>

                {showMore && (
                  <>
                    {/* Company Information */}
                    <div className="agent-info-section">
                      <h5>Company Information</h5>
                      <div className="agent-info-item"><strong>Company:</strong> {agent.Company || 'N/A'}</div>
                    </div>

                    {/* Contact Information */}
                    <div className="agent-info-section">
                      <h5>Contact Information</h5>
                      <div className="agent-info-item"><strong>Email:</strong> {agent.Email || 'N/A'}</div>
                      <div className="agent-info-item"><strong>Phone:</strong> {agent.Phone || 'N/A'}</div>
                    </div>

                    {/* Address Information */}
                    <div className="agent-info-section">
                      <h5>Address Information</h5>
                      <div className="agent-info-item"><strong>Address:</strong> {agent.Address || 'N/A'}</div>
                    </div>

                    {/* Additional Notes */}

                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDetails;
